import {
  PayloadAction,
  WithSlice,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { rootReducer } from "Store/rootReducer";
import { policyConfigApi } from "../services/policies.api";
import { Override, PolicyConfigSliceState, PolicyId } from "../types";

type PolicyConfigOverrides = Record<string, PolicyConfigSliceState>;

type InitialState = {
  selected: string;
  config: PolicyConfigOverrides;
};

const initialState: InitialState = {
  selected: "",
  config: {},
};

const initialConfig = {
  reprocess_affected: false,
  rl_scan_level: null,
  overrides: [],
  revert_overrides: [],
};

const policyConfigSlice = createSlice({
  name: "configuration",
  initialState,
  reducers: {
    setReprocessAffected: (state, action: PayloadAction<boolean>) => {
      state.config[state.selected].reprocess_affected = action.payload;
    },
    setRlLevel: (state, action: PayloadAction<number | null>) => {
      state.config[state.selected].rl_scan_level = action.payload;
    },
    setAutoApproval: (state, action: PayloadAction<boolean | undefined>) => {
      state.config[state.selected].auto_approval_enabled = action.payload;
    },
    addOverride: (state, action: PayloadAction<Override>) => {
      // this action can also be used to update an existing local override
      const overrideIndex = state.config[state.selected].overrides.findIndex(
        (override) => override.id === action.payload.id,
      );
      const overrideExists = overrideIndex >= 0;

      // if we already have an override, update it
      if (overrideExists) {
        state.config[state.selected].overrides[overrideIndex] = action.payload;
      }
      // otherwise add it to state
      else
        state.config[state.selected].overrides = state.config[
          state.selected
        ].overrides?.concat(action.payload);
    },
    removeOverride: (state, action: PayloadAction<PolicyId>) => {
      // remove any existing override
      state.config[state.selected].overrides = state.config[
        state.selected
      ].overrides.filter(
        (override: Override) => override.id !== action.payload,
      );
    },
    addRevertOverride: (state, action: PayloadAction<PolicyId>) => {
      // add to the revert_overrides array
      state.config[state.selected].revert_overrides = state.config[
        state.selected
      ].revert_overrides.concat(action.payload);
    },
    removeRevertOverride: (state, action: PayloadAction<PolicyId>) => {
      state.config[state.selected].revert_overrides = state.config[
        state.selected
      ].revert_overrides.filter(
        (revert: PolicyId) => revert !== action.payload,
      );
    },
    resetConfiguration: (state, action: PayloadAction<string>) => {
      state.config[action.payload] = initialConfig;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        policyConfigApi.endpoints.getOrganizationPolicy.matchFulfilled,
        (state, { meta }) => {
          const orgId = meta.arg.originalArgs.organizationId;
          state.config[orgId] = state.config[orgId] ?? initialConfig;
          state.selected = orgId;
        },
      )
      .addMatcher(
        policyConfigApi.endpoints.getGroupPolicy.matchFulfilled,
        (state, { meta }) => {
          const groupId = meta.arg.originalArgs.groupId;
          state.config[groupId] = state.config[groupId] ?? initialConfig;
          state.selected = groupId;
        },
      );
  },
  selectors: {
    selected: (state): string => state.selected,
    config: (state): PolicyConfigOverrides => state.config,
  },
});

const injectedSlice = policyConfigSlice.injectInto(rootReducer);

const configHasUnsavedChanges = (config: PolicyConfigSliceState): boolean => {
  return (
    config.overrides.length > 0 ||
    config.revert_overrides.length > 0 ||
    config.rl_scan_level !== null ||
    config.auto_approval_enabled !== undefined
  );
};

const _selectedConfig = createSelector(
  [injectedSlice.selectors.selected, injectedSlice.selectors.config],
  (selected, config) => {
    return config[selected] ?? initialConfig;
  },
);

export const selectOverrides = createSelector(
  [_selectedConfig],
  (rootState): Override[] => {
    return rootState.overrides;
  },
);

export const selectRevertOverrides = createSelector(
  [_selectedConfig],
  (rootState): PolicyId[] => {
    return rootState.revert_overrides;
  },
);

export const selectScanLevel = createSelector([_selectedConfig], (config) => {
  return config.rl_scan_level;
});

export const selectAutoApprovalEnabled = createSelector(
  [_selectedConfig],
  (config) => {
    return config.auto_approval_enabled;
  },
);

export const selectOverrideById = createSelector(
  [selectOverrides, (state, policyId): PolicyId => policyId],
  (overrides, policyId): Override | undefined => {
    return overrides.find((override) => override.id === policyId);
  },
);

export const selectIsRevertById = createSelector(
  [selectRevertOverrides, (state, policyId): PolicyId => policyId],
  (reverts, policyId): boolean => {
    return reverts.includes(policyId);
  },
);

export const selectHasUnsavedChanges = createSelector(
  [
    selectOverrides,
    selectRevertOverrides,
    selectScanLevel,
    selectAutoApprovalEnabled,
  ],
  (overrides, reverts, scanLevel, autoApprovalEnabled) => {
    return (
      overrides.length > 0 ||
      reverts.length > 0 ||
      scanLevel !== null ||
      autoApprovalEnabled !== undefined
    );
  },
);

export const selectUnsavedChangesIds = createSelector(
  [injectedSlice.selectors.config],
  (policyConfig): (string | undefined)[] => {
    return Object.keys(policyConfig).filter((id: string) => {
      if (configHasUnsavedChanges(policyConfig[id])) {
        return id;
      }
    });
  },
);

// note the difference; this selector looks for unsaved changes across ANY config
export const selectHasAnyUnsavedChanges = createSelector(
  [injectedSlice.selectors.config],
  (config) => {
    const hasUnsaved = Object.values(config).some((configItem) =>
      configHasUnsavedChanges(configItem),
    );
    return hasUnsaved;
  },
);

export const selectCurrentLevel = createSelector(
  [_selectedConfig],
  (config) => config.rl_scan_level,
);

export const {
  setRlLevel,
  addOverride,
  removeOverride,
  addRevertOverride,
  removeRevertOverride,
  resetConfiguration,
  setAutoApproval,
} = policyConfigSlice.actions;

declare module "Store/rootReducer" {
  export interface LazyLoadedSlices
    extends WithSlice<typeof policyConfigSlice> {}
}
